export interface ModuleConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'essential' | 'evaluations' | 'soins' | 'suivis-standardises';
  required: boolean;
  hidden?: boolean; // Masquer de la sidebar (ex: anciennes versions)
}

export interface NoteModule {
  id: string;
  name: string;
  data: Record<string, unknown>;
  completed: boolean;
}

export interface ModuleProfile {
  id: string;
  name: string;
  description: string;
  icon: string;
  modules: string[];
  conflictResolution?: {
    [moduleId: string]: {
      excludeFields?: string[];
      preferredSource?: string;
    };
  };
  professionalType?: 'INF' | 'INF_AUX';
}

/**
 * État de modification d'un profil.
 * Permet de tracker les ajouts/suppressions de modules
 * par rapport au profil de base sélectionné.
 */
export interface ProfileModificationState {
  /** ID du profil de base (avant modifications) */
  baseProfileId: string;
  /** Modules ajoutés manuellement (non présents dans le profil de base) */
  addedModules: string[];
  /** Modules retirés manuellement (présents dans le profil de base) */
  removedModules: string[];
}

export interface SharedField {
  id: string;
  moduleId: string;
  fieldPath: string;
  priority: number;
  label: string;
}

export interface BaseNoteData {
  // Informations de base toujours présentes
  dateVisite: string;
  heureVisite: string;
  typeVisite: 'domicile' | 'clinique' | 'urgence' | 'suivi';
  consentementObtenu: boolean;
  doubleIdEffectuee: boolean;

  // Modules sélectionnés
  modules: NoteModule[];
}

export const AVAILABLE_MODULES: ModuleConfig[] = [
  // Modules essentiels
  {
    id: 'informations-generales',
    name: 'Informations générales',
    description: 'Informations de base sur la visite',
    icon: 'FileText',
    category: 'essential',
    required: true,
  },
  {
    id: 'signes-vitaux',
    name: 'Signes vitaux',
    description: 'TA, pouls, respiration, température, SpO2 et glycémie',
    icon: 'Activity',
    category: 'essential',
    required: false,
  },
  {
    id: 'douleur',
    name: 'Douleur',
    description: 'Gestion de la douleur',
    icon: 'Zap',
    category: 'essential',
    required: false,
    hidden: true, // Remplacé par douleur-v2
  },
  {
    id: 'douleur-v2',
    name: 'Douleur',
    description: 'Gestion de la douleur',
    icon: 'Zap',
    category: 'essential',
    required: false,
  },

  // Modules d'évaluations
  {
    id: 'alimentation',
    name: 'Alimentation',
    description: 'Appétit, hydratation, troubles digestifs',
    icon: 'Coffee',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'elimination',
    name: 'Élimination',
    description: 'Élimination fécale et urinaire',
    icon: 'Droplets',
    category: 'evaluations',
    required: false,
    hidden: true, // Remplacé par elimination-v2
  },
  {
    id: 'elimination-v2',
    name: 'Élimination',
    description: 'Élimination fécale et urinaire',
    icon: 'Droplets',
    category: 'evaluations',
    required: false,
    hidden: true, // Remplacé par elimination-urinaire + elimination-intestinale
  },
  {
    id: 'elimination-urinaire',
    name: 'Élimination Urinaire',
    description: 'Mictions, continence urinaire, troubles urinaires',
    icon: 'Droplets',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'elimination-intestinale',
    name: 'Élimination Intestinale',
    description: 'Transit, consistance selles, continence fécale',
    icon: 'Activity',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'mobilite',
    name: 'Mobilité',
    description: 'Mobilité équilibre, risque chute',
    icon: 'Move',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'fonctions_base',
    name: 'Fonctions de base',
    description: 'Alimentation, élimination et mobilité simplifiés',
    icon: 'Activity',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'respiratoire',
    name: 'Respiratoire',
    description: 'Dyspnée, toux, oxygénothérapie',
    icon: 'Wind',
    category: 'evaluations',
    required: false,
    hidden: true, // Redondant avec cardio-respiratoire
  },
  {
    id: 'cardiovasculaire',
    name: 'Cardiovasculaire',
    description: 'Œdème, palpitations, médication',
    icon: 'Heart',
    category: 'evaluations',
    required: false,
    hidden: true, // Redondant avec cardio-respiratoire
  },
  {
    id: 'cardio-respiratoire',
    name: 'Cardio-Respiratoire',
    description: 'Évaluation complète cardio-respiratoire',
    icon: 'Activity',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'suivi-post-op-ortho',
    name: 'Suivi post-op PTH/PTG',
    description: 'Suivi post-opératoire pour prothèse totale de hanche ou de genou.',
    icon: 'ClipboardList',
    category: 'suivis-standardises',
    required: false,
    hidden: true, // Module spécialisé masqué
  },

  // Modules de soins
  {
    id: 'plaies-pansements',
    name: 'Plaies et pansements',
    description: 'Évaluation plaies, soins de pansements',
    icon: 'Shield',
    category: 'soins',
    required: false,
    hidden: true, // Module redondant avec evaluation-plaie-v2
  },
  {
    id: 'evaluation-plaie',
    name: 'Évaluation de plaie',
    description: 'Évaluation complète et détaillée de plaie selon les standards cliniques',
    icon: 'FileSearch',
    category: 'evaluations',
    required: false,
    hidden: true, // Remplacé par evaluation-plaie-v2
  },
  {
    id: 'evaluation-plaie-v2',
    name: 'Évaluation de plaie',
    description: 'Évaluation complète et détaillée de plaie selon les standards cliniques',
    icon: 'FileSearch',
    category: 'evaluations',
    required: false,
    hidden: true, // Remplacé par evaluation-plaie-wizard
  },
  {
    id: 'evaluation-plaie-wizard',
    name: 'Évaluation de plaie (Guidé)',
    description: 'Évaluation structurée avec navigation adaptative par plaie',
    icon: 'Bandage',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'sonde-urinaire',
    name: 'Sonde urinaire',
    description: 'Soins de sonde urinaire à demeure, drainage urinaire',
    icon: 'Droplets',
    category: 'soins',
    required: false,
  },
  {
    id: 'soins-plaie-simple',
    name: 'Soins de plaie (simple)',
    description: 'Changement de pansement et observation - Module INF_AUX',
    icon: 'Bandage',
    category: 'soins',
    required: false,
  },
  {
    id: 'autonomie-avq',
    name: 'Autonomie AVQ',
    description: 'Hygiène, alimentation, médication',
    icon: 'User',
    category: 'evaluations',
    required: false,
    hidden: true, // Module masqué
  },
  {
    id: 'cognitif-psycho',
    name: 'Cognitif et psychologique',
    description: 'État mental, comportement, anxiété, SCPD',
    icon: 'Brain',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'nephrostomie',
    name: 'Néphrostomie',
    description: 'Gestion et surveillance de la néphrostomie',
    icon: 'Droplets',
    category: 'soins',
    required: false,
    hidden: true, // Remplacé par nephrostomie-v2
  },
  {
    id: 'nephrostomie-v2',
    name: 'Néphrostomie',
    description: 'Gestion et surveillance de la néphrostomie',
    icon: 'Droplets',
    category: 'soins',
    required: false,
  },
  {
    id: 'soins-poc',
    name: 'POC',
    description: 'Soins du Port-O-Cath',
    icon: 'Shield',
    category: 'soins',
    required: false,
    hidden: true, // Remplacé par soins-poc-v2
  },
  {
    id: 'soins-poc-v2',
    name: 'POC',
    description: 'Soins du Port-O-Cath',
    icon: 'Shield',
    category: 'soins',
    required: false,
  },
  {
    id: 'soins-de-stomie',
    name: 'Stomie',
    description: 'Surveillance de stomie',
    icon: 'Shield',
    category: 'soins',
    required: false,
    hidden: true, // Remplacé par soins-de-stomie-v2
  },
  {
    id: 'soins-de-stomie-v2',
    name: 'Stomie',
    description: 'Soins de stomie',
    icon: 'Shield',
    category: 'soins',
    required: false,
  },
  {
    id: 'suivi-insuffisance-cardiaque',
    name: 'Suivi insuffisance cardiaque',
    description: "Suivi SOAP de l'insuffisance cardiaque",
    icon: 'Heart',
    category: 'suivis-standardises',
    required: false,
    hidden: true, // Module spécialisé masqué
  },
  {
    id: 'soins-palliatifs',
    name: 'Soins palliatifs',
    description: 'Évaluation clinique et gestion des symptômes en soins palliatifs',
    icon: 'Bird',
    category: 'evaluations',
    required: false,
    hidden: true, // Remplacé par soins-palliatifs-v2
  },
  {
    id: 'soins-palliatifs-v2',
    name: 'Soins palliatifs',
    description: 'Évaluation clinique et gestion des symptômes en soins palliatifs',
    icon: 'Bird',
    category: 'evaluations',
    required: false,
    hidden: true, // Remplacé par soins-palliatifs-wizard
  },
  {
    id: 'soins-palliatifs-wizard',
    name: 'Soins palliatifs (Guidé)',
    description: 'Évaluation structurée en 4 phases avec navigation guidée',
    icon: 'Heart',
    category: 'evaluations',
    required: false,
  },
  {
    id: 'bcm',
    name: 'Bilan Comparatif des Médicaments (BCM)',
    description: 'Évaluation complète de la médication à domicile',
    icon: 'Package',
    category: 'evaluations',
    required: false,
    hidden: true, // Module masqué
  },
  {
    id: 'catheterisme-vesical',
    name: 'Cathétérisme vésical intermittent',
    description: 'Enseignement et soins de cathétérisme vésical intermittent',
    icon: 'Droplets',
    category: 'soins',
    required: false,
    hidden: true, // Remplacé par catheterisme-vesical-v2
  },
  {
    id: 'catheterisme-vesical-v2',
    name: 'Cathétérisme vésical intermittent',
    description: 'Enseignement et soins de cathétérisme vésical intermitten',
    icon: 'Droplets',
    category: 'soins',
    required: false,
  },
];

// MODULE_PROFILES archived to src/archive/profiles/MODULE_PROFILES.ts
// Part of v7.0 UX simplification (Phase 32, 2026-01-13)
